import Modal from "@via/components/modal";
import {
  BannerType,
  MessageBanner,
} from "@via/components/message-banner/message-banner";
import FormInputComponent from "@via/components/inputs/form-input-component";
import SelectComponent from "@via/components/select/select";
import UserGroupSelectComponent from "@via/components/select/user-group-select-component";
import { Spinner } from "@via/components/loading/loading";
import { useAuth } from "@via/hooks/useAuth";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { NOTIFICATION } from "@via/context/notifications-context";
import {
  addUserGroupMemberOptions,
  requiredValidation,
} from "@via/utils/form-validators";
import {
  ModalSize,
  PREMIUM_DATASET_PERMISSION_OPTIONS,
  USER_ROLES,
} from "@via/utils/constants";
import type { EventShareItem } from "common";
import { useEffect, useMemo, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import {
  eventErrorMessage,
  eventErrorStatus,
  useUpdateVmetaEventPerms,
} from "@via/pages/projects/metadata/events/events.api";
import { payloadFromRows, rowsFromPerms, type ShareRow } from "./share-payload";

const everyoneOption = { value: "everyone", label: "Everyone" };
const newRow: ShareRow = { type: "user", email: "", read: true, write: false };
export const STALE_MESSAGE =
  "This event form changed since you opened it. Its current sharing is loaded again; make your change once more.";

/** Everyone is offered to an administrator, and kept visible for anyone when the form already has it. */
export const shareTypeOptions = (isAdmin: boolean, hadEveryone: boolean) =>
  isAdmin || hadEveryone
    ? [everyoneOption, ...PREMIUM_DATASET_PERMISSION_OPTIONS]
    : PREMIUM_DATASET_PERMISSION_OPTIONS;

interface Props {
  canvasId: string;
  item: EventShareItem;
  open: boolean;
  onClose: () => void;
}

const EventShareDialog = ({ canvasId, item, open, onClose }: Props) => {
  const { user } = useAuth();
  const { setNotification } = useNotifications();
  const { mutateAsync } = useUpdateVmetaEventPerms();
  const [error, setError] = useState<string | null>(null);
  const rows = useMemo(() => rowsFromPerms(item.perms), [item]);
  const { control, handleSubmit, watch, reset, formState } = useForm<{
    perms: ShareRow[];
  }>({
    mode: "onSubmit",
    defaultValues: { perms: rows },
  });
  // A refetched item (after a save elsewhere or a 409) replaces what the form shows.
  useEffect(() => reset({ perms: rows }), [rows, reset]);
  const { fields, append, remove } = useFieldArray({ control, name: "perms" });
  const typeOptions = shareTypeOptions(
    user?.userRole === USER_ROLES.ADMIN,
    item.perms.some(p => p.type === "everyone")
  );
  const groupIds = useMemo(
    () => item.perms.flatMap(p => (p.type === "group" ? [p.groupId] : [])),
    [item]
  );

  const save = async ({ perms }: { perms: ShareRow[] }) => {
    setError(null);
    try {
      await mutateAsync({
        canvasId,
        eventId: item.id,
        body: { version: item.version, perms: payloadFromRows(perms) },
      });
      setNotification({
        id: "event-share-saved",
        message: `Sharing saved for ${item.name}`,
        variant: NOTIFICATION.SUCCESS,
      });
      onClose();
    } catch (err) {
      setError(
        eventErrorStatus(err) === 409
          ? STALE_MESSAGE
          : eventErrorMessage(err, "Sharing could not be saved.")
      );
    }
  };

  return (
    <Modal
      open={open}
      setOpen={onClose}
      title={`Share ${item.name}`}
      showButtons={false}
      size={ModalSize.Large}>
      <>
        {error && <MessageBanner messages={[error]} type={BannerType.Error} />}
        <p className="text-sm text-gray-600">
          People and groups listed here can open this event form and save
          records through it. Their access to each collection still decides what
          they can save. Changing the form itself needs write access to the
          sample tracker.
        </p>
        <form
          onSubmit={e => {
            e.preventDefault();
            void handleSubmit(save)(e);
          }}>
          <div className="space-y-6 mt-2">
            <div className="flex justify-between items-center">
              <label className="flex items-center text-sm font-medium leading-6 text-gray-900">
                Shared with
              </label>
              <button
                type="button"
                onClick={() => append(newRow)}
                className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                + Add Permission
              </button>
            </div>
            <div className="flex flex-col px-2 sm:px-4 max-h-[500px] overflow-auto">
              {fields.map((field, idx) => {
                const type = watch(`perms.${idx}.type`);
                // Decided by the row as loaded, never by the email the user is editing.
                const unknownUser =
                  type === "user" && !!watch(`perms.${idx}.unknown`);
                return (
                  <div
                    key={field.id}
                    className="flex items-center gap-2 border-b py-2">
                    <div className="min-w-[150px]">
                      <SelectComponent
                        control={control}
                        name={`perms.${idx}.type`}
                        options={typeOptions}
                        placeholder="Select type"
                        enableClear={false}
                      />
                    </div>
                    {unknownUser && (
                      <span className="w-full text-sm text-gray-500">
                        Unknown or removed user
                      </span>
                    )}
                    {type === "user" && !unknownUser && (
                      <div className="-mt-2 w-full">
                        <FormInputComponent
                          control={control}
                          name={`perms.${idx}.email`}
                          placeholder="Enter email"
                          validations={addUserGroupMemberOptions.email}
                        />
                      </div>
                    )}
                    {type === "group" && (
                      <div className="w-full">
                        <UserGroupSelectComponent
                          control={control}
                          name={`perms.${idx}.groupId`}
                          rules={requiredValidation("User Group")}
                          menuPlacement="top"
                          errors={formState.errors}
                          uniqueIdentifier="vmetaGroupId"
                          missingId={groupIds}
                        />
                      </div>
                    )}
                    {type === "everyone" && <span className="w-full" />}
                    <label className="flex items-center gap-1">
                      <FormInputComponent
                        control={control}
                        name={`perms.${idx}.read`}
                        type="checkbox"
                        className="h-6 w-6 -mt-2"
                      />
                      Read
                    </label>
                    {type !== "everyone" && (
                      <label className="flex items-center gap-1">
                        <FormInputComponent
                          control={control}
                          name={`perms.${idx}.write`}
                          type="checkbox"
                          className="h-6 w-6 mr-2 -mt-2"
                        />
                        Write
                      </label>
                    )}
                    <button
                      type="button"
                      aria-label="Remove"
                      onClick={() => remove(idx)}
                      className="px-2 text-gray-500 hover:text-gray-700">
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="flex justify-end mt-6 gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
              Cancel
            </button>
            <button
              type="submit"
              disabled={formState.isSubmitting}
              className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50">
              <Spinner loading={formState.isSubmitting}>Save</Spinner>
            </button>
          </div>
        </form>
      </>
    </Modal>
  );
};

export default EventShareDialog;
